// client/customer/src/components/CustomerHeader.tsx
import React, { useEffect, useState } from 'react';
import camiyuiLogo from '../assets/camiyui-logo.jpg';
import { BRAND_TIFFANY_BLUE, STORE_DISPLAY_NAME } from '../constants/branding';
import LanguageSwitcher from './LanguageSwitcher';
import { useI18n } from '../i18n';

type Props = {
  subtitle?: string;
  compact?: boolean;
};

const CustomerHeader: React.FC<Props> = ({ subtitle, compact = false }) => {
  const { t } = useI18n();
  const customerName = localStorage.getItem('customerName') || '';
  const [isScrolled, setIsScrolled] = useState(false);
  const headerTitle = subtitle ?? '';
  const accountLabel = customerName ? t('account.member') : t('account.guest');

  useEffect(() => {
    if (!compact) {
      setIsScrolled(false);
      return;
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [compact]);

  return (
    <header
      className="sticky top-0 z-40 border-b border-white/20 shadow-[0_14px_28px_rgba(10,186,181,0.28)]"
      style={{ backgroundColor: BRAND_TIFFANY_BLUE }}
    >
      <div
        className={`mx-auto flex max-w-md items-center justify-between px-4 transition-all duration-200 ${
          compact
            ? isScrolled
              ? 'gap-2 py-2'
              : 'gap-3 py-2.5'
            : 'gap-3 py-3.5'
        }`}
      >
        <div className={`flex items-center ${isScrolled ? 'gap-2.5' : 'gap-3'}`}>
          <div
            className={`overflow-hidden rounded-2xl border border-white/10 bg-white shadow-sm transition-all duration-200 ${
              compact && isScrolled ? 'h-9 w-9' : 'h-11 w-11'
            }`}
          >
            <img
              src={camiyuiLogo}
              alt={STORE_DISPLAY_NAME}
              className="h-full w-full object-cover"
            />
          </div>

          <div className="flex flex-col">
            <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/80">
              {STORE_DISPLAY_NAME}
            </span>
            {headerTitle ? (
              <span
                className={`font-semibold text-white transition-all duration-200 ${
                  compact && isScrolled ? 'text-sm' : 'text-base'
                }`}
              >
                {headerTitle}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher compact />
          <div
            className={`rounded-2xl border border-white/10 bg-white/5 text-right transition-all duration-200 ${
              compact && isScrolled ? 'px-2.5 py-1.5' : 'px-3 py-2'
            }`}
          >
            {!isScrolled ? (
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/75">
                {accountLabel}
              </div>
            ) : null}
            <div
              className={`max-w-[110px] truncate font-semibold text-white transition-all duration-200 ${
                compact && isScrolled ? 'text-xs' : 'text-sm'
              }`}
            >
              {customerName || t('account.guest')}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default CustomerHeader;
